import Model, { attr } from '@ember-data/model';
import adapter from 'garaje/utils/decorators/adapter';
import capitalize from 'lodash/capitalize';

const READABLE_EVENT_TYPES: { [key: string]: string } = {
  'admin.role.company.created': 'Company admin role created',
  'admin.role.company.changed': 'Company admin role changed',
  'admin.role.company.removed': 'Company admin role removed',
  'admin.role.location.created': 'Location admin role created',
  'admin.role.location.changed': 'Location admin role changed',
  'admin.role.location.removed': 'Location admin role removed',
  'admin.role.custom.created': 'Custom admin role created',
  'admin.role.custom.changed': 'Custom admin role changed',
  'admin.role.custom.removed': 'Custom admin role removed',
  'employee.destroyed': 'Employee permanently deleted',
  'data_retention_policy.updated': 'Data retention policy updated',
  'move_request.approved': 'Move request approved',
  'move_request.declined': 'Move request declined',
};

export interface ImpactedItem {
  name: string;
  type: string;
  id: string;
}

@adapter('event-log')
class EventLogModel extends Model {
  @attr('number') declare companyId: number;
  @attr('number') declare locationId: number | null;
  @attr('number') declare zoneId: number | null;
  @attr('string') declare metadataVersion: string;
  @attr('string') declare locationName: string | null;
  @attr('number') declare eventTimestamp: number;
  @attr('string') declare eventDescription: string;
  @attr('string') declare eventType: string;
  @attr('string') declare actorName: string;
  @attr('string') declare actorId: string;
  @attr('string') declare actorType: string;
  @attr('string') declare group: string;
  @attr('string') declare targetName: string;
  @attr('string') declare targetType: string;
  @attr('string') declare targetId: string;
  @attr('string') declare serviceName: string;
  @attr('string') declare ipAddress: string;
  @attr('string') declare country: string | null;
  @attr('string') declare serviceVersion: string;
  @attr('string') declare event: string;
  @attr() declare impactedItems: ImpactedItem[];

  get dateTime(): Date | null {
    try {
      const timestamp = this.eventTimestamp;
      const timestampMs = timestamp > 1e12 ? timestamp : timestamp * 1000;
      const date = new Date(timestampMs);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  get eventTypeName(): string {
    const eventType = this.eventType;
    if (READABLE_EVENT_TYPES[eventType]) return READABLE_EVENT_TYPES[eventType];
    const words = eventType.split('.');
    const formattedWords = words.map((word) => word.replace(/_/g, ' '));
    return [capitalize(formattedWords[0]), ...formattedWords.slice(1)].join(' ');
  }

  get location(): string {
    return this.locationName || '-';
  }
}

export default EventLogModel;

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'event-log': EventLogModel;
  }
}
